import React from "react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { BUSINESS } from "@/constants/business";

export default function PrivacyPolicyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy | Vila Elevators & Escalators"
        description="Vila Elevators & Escalators privacy policy — how we collect, use and protect your personal information. DPDP Act 2023 compliant."
        path="/privacy-policy"
      />

      <PageHeader
        eyebrow="Legal"
        title="Privacy Policy"
        desc={`Last updated: ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`}
      />

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-8">
          <Block title="1. Introduction">
            <P>
              Vila Elevators &amp; Escalators ("we", "our", or "us") operates the website{" "}
              <a href="https://vilaelevators.in" className="text-[#b08d57] underline">vilaelevators.in</a>{" "}
              (the "Service"). This Privacy Policy explains how we collect, use, disclose and safeguard your
              information when you visit our website, request a quote, or otherwise interact with us. This
              policy is compliant with the Digital Personal Data Protection Act, 2023 ("DPDP Act") of India
              and applicable rules.
            </P>
          </Block>

          <Block title="2. Information We Collect">
            <P>We collect the following categories of personal data:</P>
            <List
              items={[
                "Identity data — name you voluntarily provide via forms",
                "Contact data — phone number, email address, city",
                "Enquiry data — building type, service interest, message content",
                "Technical data — IP address, browser type, device type, pages visited",
                "Usage data — how you use our website, time spent, click patterns",
                "Marketing data — your preferences in receiving marketing from us",
              ]}
            />
          </Block>

          <Block title="3. How We Collect Data">
            <List
              items={[
                "Directly from you — when you fill out the contact form, cost calculator, or WhatsApp us",
                "Automatically — via cookies, server logs, and analytics tools like Google Analytics",
                "From third parties — advertising platforms (Meta, Google Ads, LinkedIn) that share aggregate campaign performance data",
              ]}
            />
          </Block>

          <Block title="4. How We Use Your Information">
            <List
              items={[
                "Respond to your enquiries and provide quotations",
                "Schedule site surveys and coordinate installations",
                "Provide maintenance, service and warranty support",
                "Send you relevant product updates, offers and newsletters (only if you consent)",
                "Improve our website, services and customer experience",
                "Analyse advertising campaign effectiveness and retarget interested visitors",
                "Comply with legal obligations under Indian law",
              ]}
            />
          </Block>

          <Block title="5. Cookies & Tracking Technologies">
            <P>
              We use cookies and similar tracking technologies to enhance your browsing experience. These include:
            </P>
            <List
              items={[
                "Essential cookies — required for the site to function (form security, session state)",
                "Analytics cookies — Google Analytics 4 (GA4) to understand how visitors use our site",
                "Advertising cookies — Meta Pixel, Google Ads, LinkedIn Insight Tag for retargeting and campaign optimisation",
              ]}
            />
            <P>
              You may accept or reject non-essential cookies via the cookie banner shown on your first visit. You can change your preference at any time by clearing your browser's local storage for this site.
            </P>
          </Block>

          <Block title="6. Sharing Your Information">
            <P>We do not sell your personal information. We share it only with:</P>
            <List
              items={[
                "Our own installation, service and sales teams to fulfil your requests",
                "Reputable analytics providers (Google, Meta, LinkedIn) — receiving pseudonymised event data only",
                "Cloud infrastructure providers hosting our website & database (data resides on servers with appropriate safeguards)",
                "Government authorities when legally required (court order, subpoena, regulatory request)",
              ]}
            />
          </Block>

          <Block title="7. Data Retention">
            <P>
              We retain your personal data only as long as necessary for the purposes described in this policy — typically 24 months for enquiry data unless you become a customer, in which case we retain records required by Indian accounting and warranty regulations for up to 8 years.
            </P>
          </Block>

          <Block title="8. Your Rights Under the DPDP Act">
            <P>Subject to Indian law, you have the right to:</P>
            <List
              items={[
                "Access — request a copy of the personal data we hold about you",
                "Correction — request that inaccurate or incomplete data be corrected",
                "Erasure — request deletion of your personal data (subject to legal retention obligations)",
                "Grievance redressal — raise a complaint with our Data Protection Officer",
                "Withdraw consent — for marketing communications at any time",
              ]}
            />
            <P>
              To exercise any of these rights, email us at{" "}
              <a href={`mailto:${BUSINESS.email}`} className="text-[#b08d57] underline">{BUSINESS.email}</a>{" "}
              with the subject line "DPDP Request".
            </P>
          </Block>

          <Block title="9. Data Security">
            <P>
              We implement industry-standard technical and organisational measures — including HTTPS encryption, access controls, and secure hosting — to protect your data. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </P>
          </Block>

          <Block title="10. Children's Privacy">
            <P>
              Our website is not directed to children under 18. We do not knowingly collect personal data from children. If you believe a child has provided us data, please contact us and we will delete it promptly.
            </P>
          </Block>

          <Block title="11. Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page reflects the latest version. Material changes will be communicated via a notice on our website.
            </P>
          </Block>

          <Block title="12. Contact & Grievance Officer">
            <P>
              For any questions about this policy or to exercise your data-subject rights:
            </P>
            <div className="border border-[#e5ddd0] bg-[#faf7f2]/40 p-6 mt-4">
              <div className="text-xs font-bold uppercase tracking-widest text-[#b08d57] mb-3">Data Protection Officer</div>
              <div className="text-[#1a1a1a] font-display text-lg mb-2">{BUSINESS.name}</div>
              <div className="text-[#6b6b6b] text-sm space-y-1">
                <div>{BUSINESS.address.line1}</div>
                <div>{BUSINESS.address.line2}</div>
                <div>{BUSINESS.address.city} – {BUSINESS.address.postal}, {BUSINESS.address.state}</div>
                <div className="pt-2">
                  Email: <a href={`mailto:${BUSINESS.email}`} className="text-[#b08d57]">{BUSINESS.email}</a>
                </div>
                <div>
                  Phone: <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#b08d57]">{BUSINESS.phone}</a>
                </div>
              </div>
            </div>
          </Block>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#1a1a1a] mb-4">{title}</h2>
      <div className="space-y-3 text-[#4a4a4a] leading-relaxed">{children}</div>
    </div>
  );
}
function P({ children }) {
  return <p className="text-[#4a4a4a] leading-relaxed">{children}</p>;
}
function List({ items }) {
  return (
    <ul className="space-y-2 text-[#4a4a4a]">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="w-1.5 h-1.5 bg-[#b08d57] mt-2 flex-shrink-0" /> <span>{i}</span>
        </li>
      ))}
    </ul>
  );
}
